import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { GuestService } from '../core/services/guest.service';
import { EncryptionService } from '../core/services/encryption.service';
import { AnalyticsService } from '../core/services/analytics.service';

const DEMO_DIRECT_HEX = '5f9059d8';
const DEMO_DIRECT_NUM_ID = '3586';
const DEMO_DIRECT_VISITOR_NAME = 'DemoDirectGalleryUser';

@Component({
  selector: 'app-demo-direct-gallery',
  templateUrl: './demo-direct-gallery.component.html',
  styleUrls: ['./demo-direct-gallery.component.scss'],
})
export class DemoDirectGalleryComponent implements OnInit {

  readonly tiles = Array(8).fill(0);

  constructor(
    private router: Router,
    private afAuth: AngularFireAuth,
    private guestService: GuestService,
    private encryptionService: EncryptionService,
    private analytics: AnalyticsService,
  ) { }

  ngOnInit(): void {
    (window as any).clarity?.('set', 'page_type', 'demo_gallery');
    (window as any).clarity?.('set', 'user_type', 'demo');

    // PostHog: record entry into the direct demo gallery.
    this.analytics.capture('demo_gallery_viewed', { source: 'direct', event_id: DEMO_DIRECT_HEX });

    // Set localStorage flags so event-detail treats this as a demo guest
    localStorage.setItem('eventId', DEMO_DIRECT_HEX);
    localStorage.setItem('isGuestUser', 'true');
    localStorage.setItem('isDemoUser', 'true');
    localStorage.setItem('isDemoDirectGallery', 'true');
    localStorage.setItem('showTopBar', 'false');
    localStorage.setItem('isHost', 'false');

    // Reuse an existing demo guest session if one is already stored
    const existingUser = localStorage.getItem('user');
    this.afAuth.currentUser
      .then((firebaseUser) => {
        if (firebaseUser && existingUser && existingUser !== 'null') {
          // A returning demo visitor needs `uid` too (see rememberGuestUid) —
          // but only our own demo guest, never a real account browsing here.
          if (localStorage.getItem('user_name') === DEMO_DIRECT_VISITOR_NAME) {
            this.rememberGuestUid(firebaseUser.uid);
          }
          this.router.navigate(['/event-detail', 'true']);
        } else {
          this.createGuestSessionAndNavigate();
        }
      })
      .catch(() => {
        this.createGuestSessionAndNavigate();
      });
  }

  private navigate(): void {
    this.router.navigate(['/event-detail', 'true']);
  }

  /**
   * The guest sign-in popup links a guest to their new account — and then
   * hard-reloads event-detail as that account — only when `uid` holds the
   * guest's Firebase uid (demo-landing sets it too). Without it, signing in
   * from this gallery left the visitor on a stale guest view: the popup fell
   * through to /home, which GuestRestrictionGuard blocked.
   */
  private rememberGuestUid(uid: string): void {
    if (uid) localStorage.setItem('uid', uid);
  }

  private createGuestSessionAndNavigate(): void {
    localStorage.setItem('e', DEMO_DIRECT_NUM_ID);

    const fallbackTimeout = setTimeout(() => this.navigate(), 12000);

    this.guestService.createDemoGuestSession(DEMO_DIRECT_HEX, DEMO_DIRECT_NUM_ID, DEMO_DIRECT_VISITOR_NAME)
      .subscribe({
        next: (guestRes: any) => {
          // PostHog: identify with analytics_id (no PII — matches iOS and the
          // demo-event flow). Only ever identify with analytics_id; never fall
          // back to the Firebase uid, which would split the session across two
          // PostHog persons.
          const analyticsId = guestRes?.analytics_id || guestRes?.data?.analytics_id;
          if (analyticsId) {
            localStorage.setItem('analytics_id', analyticsId);
            this.analytics.identify(analyticsId);
          }

          const firebaseToken = guestRes?.data?.firebase_token;
          if (!firebaseToken) {
            clearTimeout(fallbackTimeout);
            this.navigate();
            return;
          }
          this.afAuth.signInWithCustomToken(firebaseToken)
            .then((userCredential) => {
              if (userCredential?.user?.uid) {
                localStorage.setItem('u', userCredential.user.uid);
                localStorage.setItem('user_name', DEMO_DIRECT_VISITOR_NAME);
                this.rememberGuestUid(userCredential.user.uid);
              }
              try {
                const delegate = (userCredential.user as any)._delegate ?? userCredential.user;
                const encrypted = this.encryptionService.encryptData(JSON.stringify(delegate));
                localStorage.setItem('user', encrypted);
              } catch { /* non-fatal — authState will write it shortly */ }
              clearTimeout(fallbackTimeout);
              this.navigate();
            })
            .catch(() => {
              clearTimeout(fallbackTimeout);
              this.navigate();
            });
        },
        error: () => {
          clearTimeout(fallbackTimeout);
          this.navigate();
        },
      });
  }
}
